import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import {
  HardHat, MapPin, Star, TrendingUp, Target,
  CheckCircle, ArrowRight, Phone, Calendar,
  FileText, Shield, BarChart3, Image, Download,
  Building2, Hammer, Users, Ruler
} from "lucide-react";

const ConstructionMarketingPage = () => {

  useEffect(() => {
    document.title = "Construction Marketing Dallas | Contractor SEO & Lead Generation | Digital Media Ninja";
  }, []);

  const stats = [
    { value: "Leads", label: "Local SEO & Ads", icon: TrendingUp },
    { value: "Contractors", label: "Industry Focus", icon: HardHat },
    { value: "CPL", label: "Cost Optimization", icon: Target },
    { value: "Growth", label: "Revenue-Focused Plans", icon: BarChart3 }
  ];

  const services = [
    { icon: MapPin, title: "Local SEO for Contractors", desc: "Rank #1 for 'general contractor near me,' 'home builder Dallas,' and high-intent commercial searches." },
    { icon: Image, title: "Project Portfolio Marketing", desc: "Showcase completed builds with professional photography, video walkthroughs, and optimized case studies." },
    { icon: Star, title: "Review & Reputation Management", desc: "Build a 5-star reputation on Google, Houzz, Angi, and BBB that wins bids before the first call." },
    { icon: FileText, title: "Construction Content Marketing", desc: "Cost guides, project spotlights, and permit resources that attract qualified leads from search." },
    { icon: Shield, title: "Google Ads & Local Service Ads", desc: "Capture high-intent searches with Google Guaranteed badge and targeted PPC for your service area." },
    { icon: Users, title: "B2B Partnership Strategy", desc: "Connect with architects, developers, and suppliers through strategic PR and referral programs." }
  ];

  const projectTypes = [
    "Residential Remodeling", "Custom Home Building", "Commercial Construction",
    "Tenant Improvements", "Kitchen & Bath Remodels", "Room Additions",
    "Roofing & Exterior", "Industrial Build-Outs"
  ];

  const serviceAreas = [
    "Dallas", "Fort Worth", "Plano", "Arlington", "Irving",
    "Frisco", "McKinney", "Denton", "Richardson", "Garland",
    "Grand Prairie", "Mesquite", "Carrollton", "Lewisville"
  ];

  const faqs = [
    {
      q: "How do I get more leads for my construction company?",
      a: "The most effective approach combines local SEO (optimizing your Google Business Profile for 'contractor near me' searches), Google Local Services Ads for immediate visibility with the Google Guaranteed badge, and a professional website with project portfolios and client testimonials. Results vary by market and competition — we track every call and lead so you can see exactly what your campaigns produce."
    },
    {
      q: "What marketing strategies work best for construction companies?",
      a: "SEO and local SEO deliver the highest long-term ROI for construction companies. Google Ads provide immediate leads while SEO builds. A referral program, project portfolio, and active social media presence (especially Instagram for residential and LinkedIn for commercial) round out a strong strategy. Read our full guide on how to market a construction business for 15 proven strategies."
    },
    {
      q: "How much should a construction company spend on marketing?",
      a: "Successful construction companies typically invest 5-10% of annual revenue in marketing. For a $2M/year contractor, that's $100K-200K annually across SEO, PPC, content, and reputation management. Newer companies may need to invest closer to 10-15% initially. The key is tracking ROI—our clients average a 5-8x return on marketing investment."
    },
    {
      q: "Do construction companies need a website?",
      a: "Absolutely. Over 80% of property owners research contractors online before making contact. A professional, mobile-optimized website with project photos, client reviews, and clear calls-to-action is the foundation of every other marketing effort. Without it, you're invisible to most potential clients and losing bids to competitors who are online."
    },
    {
      q: "How important are reviews for construction businesses?",
      a: "Reviews are critical—93% of consumers read reviews before hiring a contractor, and businesses with 50+ reviews earn 266% more leads than those with fewer. We implement automated review request systems and help you respond professionally to all feedback, helping review counts grow steadily month after month."
    }
  ];

  const caseStudyResults = [
    { metric: "Monthly Leads", before: "25", after: "88", change: "+252%" },
    { metric: "Cost Per Lead", before: "$145", after: "$62", change: "-57%" },
    { metric: "Google Maps Ranking", before: "#11", after: "#2", change: "Page 1" }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Construction Marketing & Contractor SEO Services Dallas",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" },
          "telephone": "(432) 934-9470"
        },
        "areaServed": serviceAreas.map(city => ({ "@type": "City", "name": city })),
        "description": "Specialized digital marketing for construction companies and general contractors in Dallas-Fort Worth. Local SEO, Google Ads, reputation management, and lead generation for residential and commercial builders.",
        "serviceType": "Construction Marketing Services"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@type": "WebPage",
        "name": "Construction Marketing Dallas | Contractor SEO & Lead Generation",
        "description": "Expert construction marketing and SEO services for Dallas-Fort Worth contractors. Generate more qualified leads for residential and commercial construction projects.",
        "url": "https://digitalmedianinja.com/construction-marketing",
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", ".faq-answer"]
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black" data-testid="construction-marketing-page">
      <Helmet>
        <title>Construction Marketing Dallas | Contractor SEO & Lead Generation | Digital Media Ninja</title>
        <meta name="description" content="Expert construction marketing and SEO for Dallas-Fort Worth contractors. Local SEO, Google Ads, reputation management, and B2B lead generation. contractors and builders." />
        <meta name="keywords" content="construction marketing Dallas, contractor SEO, construction leads, general contractor marketing, home builder marketing, commercial construction SEO, construction digital marketing DFW, contractor lead generation" />
        <link rel="canonical" href="https://digitalmedianinja.com/construction-marketing" />
        <meta property="og:title" content="Construction Marketing Dallas | Contractor SEO & Lead Generation" />
        <meta property="og:description" content="Expert construction marketing for Dallas-Fort Worth contractors. Local SEO, Google Ads, B2B partnerships." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1707526176242-e9761fd81483?w=1200" />
        <meta property="og:url" content="https://digitalmedianinja.com/construction-marketing" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-yellow-600/5"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-6">
              <HardHat className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium" data-testid="industry-badge">Construction Industry Specialists</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="hero-title">
              Generate More <span className="text-amber-400">Construction Leads</span> in Dallas-Fort Worth
            </h1>

            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Dominate local search for general contracting, home building, and commercial construction. We help Dallas contractors fill their pipeline with qualified projects year-round.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <BookConsultationButton meetingType="construction" size="lg">
                Get Free Construction Marketing Audit <ArrowRight className="w-5 h-5 ml-2" />
              </BookConsultationButton>
              <Link to="/blog/how-to-market-a-construction-business">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-amber-400/30 text-amber-400 hover:bg-amber-400/10"
                  data-testid="read-guide-btn"
                >
                  <FileText className="w-5 h-5 mr-2" /> Read Our Full Guide
                </Button>
              </Link>
              <Link to="/guides/construction-marketing">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="read-playbook-btn"
                >
                  <Download className="w-5 h-5 mr-2" /> Marketing Playbook
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-4 bg-white/5 rounded-xl border border-white/10" data-testid={`stat-card-${i}`}>
                  <stat.icon className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20" data-testid="services-section">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Construction Marketing Services</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Comprehensive digital marketing designed specifically for general contractors, builders, and construction firms.</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Card key={i} className="bg-[#111] border-white/10 hover:border-amber-400/30 transition-all" data-testid={`service-card-${i}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center mb-4">
                    <svc.icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-gray-400 text-sm">{svc.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]" data-testid="project-types-section">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">We Help Contractors in Every Segment</h2>
          <p className="text-gray-400 text-center mb-12">From residential remodels to commercial build-outs, our strategies are tailored to your project type.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
            {projectTypes.map((type, i) => (
              <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/10 text-center hover:border-amber-400/30 transition-colors">
                <Hammer className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                <p className="text-white font-medium text-sm">{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20" data-testid="service-areas-section">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">DFW Service Areas We Cover</h2>
          <p className="text-gray-400 text-center mb-12">We help construction companies dominate search in every city across Dallas-Fort Worth.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {serviceAreas.map((area, i) => (
              <div key={i} className="p-3 bg-white/5 rounded-xl border border-white/10 text-center hover:border-amber-400/30 transition-colors">
                <p className="text-white font-medium text-sm">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Results */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black" data-testid="case-study-section">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Real Construction Marketing Results</h2>
          <p className="text-gray-400 text-center mb-12">See how we helped a Dallas general contractor more than double their qualified leads.</p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {caseStudyResults.map((result, i) => (
              <div key={i} className="text-center p-8 bg-[#111] rounded-2xl border border-white/10" data-testid={`result-card-${i}`}>
                <p className="text-gray-400 mb-2">{result.metric}</p>
                <div className="flex items-center justify-center gap-4 mb-2">
                  <span className="text-gray-500 line-through">{result.before}</span>
                  <ArrowRight className="w-4 h-4 text-amber-400" />
                  <span className="text-2xl font-bold text-white">{result.after}</span>
                </div>
                <p className="text-amber-400 font-semibold">{result.change}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Cross-Link */}
      <section className="py-16" data-testid="blog-cross-link">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-amber-500/[0.08] to-yellow-600/[0.05] border border-amber-400/20 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-white mb-3">15 Proven Construction Marketing Strategies for 2026</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Our comprehensive guide covers every digital and offline tactic construction companies need to generate leads, build authority, and win more projects.
              </p>
              <Link to="/blog/how-to-market-a-construction-business">
                <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="read-full-guide-btn">
                  Read the Full Guide <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="w-full md:w-48 h-32 md:h-36 rounded-xl overflow-hidden shrink-0">
              <img
                src="https://images.unsplash.com/photo-1707526176242-e9761fd81483?w=400&q=80"
                alt="Construction marketing strategies guide"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]" data-testid="faq-section">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Construction Marketing FAQs</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10" data-testid={`faq-item-${i}`}>
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5">
                  {faq.q}
                  <span className="text-amber-400 group-open:rotate-180 transition-transform">&#9660;</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400 faq-answer">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-amber-500/10 to-yellow-600/10" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Grow Your Construction Business?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Get a free construction marketing audit and a custom strategy for your Dallas-Fort Worth service area.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <BookConsultationButton meetingType="construction" size="lg">
              <Calendar className="w-5 h-5 mr-2" /> Book Strategy Call
            </BookConsultationButton>
            <a href="tel:+14329349470">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="call-btn">
                <Phone className="w-4 h-4 mr-2" /> Call (432) 934-9470
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConstructionMarketingPage;
